import type { GroupOption } from '@exam-management/common/types';
import {
  ALL_GROUP_OPTION,
  filterAndInsertOption,
  UNASSIGNED_OPTION,
} from '@exam-management/common/utils';
import {
  OrderType,
  useGetQBankStructureGroupTagByIdQuery,
  useSearchQBankStructureGroupTagsLazyQuery,
} from '@exam-management/graphql';
import { SearchSelection } from '@shared/components';
import { useEffect, useMemo, useState } from 'react';

import { useQBankStructureSearchParams } from '../../hooks';

export default function GroupSelection() {
  const [{ group }, setQBankStructureSearchParams] = useQBankStructureSearchParams();

  const [groupInputValue, setGroupInputValue] = useState<string>('');
  const isUnassigned = group === UNASSIGNED_OPTION.value;

  const { data: groupData, loading: isGroupLoading } = useGetQBankStructureGroupTagByIdQuery({
    variables: {
      qbsGetQBankStructureGroupTagById: group,
    },
    skip: group === '' || isUnassigned,
  });

  const [
    searchQBankStructureGroupTags,
    { data: groupOptionData, loading: isGroupOptionDataLoading },
  ] = useSearchQBankStructureGroupTagsLazyQuery();

  const groupOptions = useMemo(() => {
    if (groupOptionData) {
      const groupOptions = groupOptionData.qbsSearchQBankStructureGroupTags.tags.map<GroupOption>(
        (tag) => {
          return {
            label: tag.name,
            value: tag.id,
          };
        },
      );

      return filterAndInsertOption(
        [ALL_GROUP_OPTION, UNASSIGNED_OPTION],
        groupOptions,
        groupInputValue ?? '',
        ALL_GROUP_OPTION,
      );
    } else {
      return [];
    }
  }, [groupOptionData, groupInputValue]);

  const currentQBankGroup = groupData
    ? {
        label: groupData.qbsGetQBankStructureGroupTagById.name,
        value: groupData.qbsGetQBankStructureGroupTagById.id,
      }
    : ALL_GROUP_OPTION;

  const value = isUnassigned ? UNASSIGNED_OPTION : currentQBankGroup;
  const isLoading = isGroupLoading || isGroupOptionDataLoading;

  useEffect(() => {
    if (groupData && !isGroupLoading) {
      setGroupInputValue(groupData.qbsGetQBankStructureGroupTagById.name);
    } else {
      setGroupInputValue(!isGroupLoading ? ALL_GROUP_OPTION.label : '');
    }
  }, [groupData]);

  return (
    <SearchSelection
      label='Group'
      mode='fetch-debounce'
      value={value ?? null}
      inputValue={groupInputValue}
      defaultValue={ALL_GROUP_OPTION}
      options={groupOptions}
      loading={isLoading}
      disabled={isGroupLoading}
      onSearch={(inputValue) => {
        const isDefaultValue = inputValue === ALL_GROUP_OPTION.label;
        searchQBankStructureGroupTags({
          variables: {
            search: {
              name: !isDefaultValue && inputValue?.trim() ? inputValue : undefined,
            },
            paging: {
              currentPage: 0,
              pageSize: 100,
            },
            order: {
              field: 'name',
              type: OrderType.Asc,
            },
          },
          fetchPolicy: 'cache-and-network',
        });
      }}
      onChange={(_, option) => {
        setQBankStructureSearchParams({
          group: option?.value?.toString() ?? '',
        });
        setGroupInputValue(option?.label ?? '');
      }}
      onInputChange={(_, value) => {
        setGroupInputValue(value);
      }}
    />
  );
}
